import React, { useState, useEffect } from "react";
import Toast, { type ToastMessage } from "../common/Toast";
import { activeBadgeCss } from "../../utils/statusVariant";
import {
  Plus,
  Trash2,
  X,
  MapPin,
  Save,
  Check,
  Edit2,
  Shield,
  Activity as ActivityIcon,
  CircleMinus,
  CircleCheck,
  User as UserIcon,
  Home,
  CreditCard,
  GraduationCap,
  Briefcase,
  Building2,
  Key,
  ClipboardList,
} from "lucide-react";
import UserAclPanel from "../itgc/UserAclPanel";
import { itgcService, UserAclRow } from "../../services/itgcService";
import MySopsTab from "../governance/sop-instances/MySopsTab";
import {
  User,
  userService,
  CreateUserRequest,
  UpdateUserRequest,
  validateUserInput,
} from "../../services/userService";
import postingService, { Posting } from "../../services/postingService";
import rolesService, {
  AdminRole,
  UserRbacRole,
  MODULE_NAMES,
} from "../../services/rolesService";
import FormControl from "../common/FormControl";
import Modal from "../common/Modal";
import Button from "../common/Button";
import NoDataFound from "../common/NoDataFound";
import { confirmWarning, confirmActive } from "../common/confirm";
import extractApiError from "../../utils/apiError";
import { useFieldValidation } from "../../validation/useFieldValidation";
import { usePermission, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";
import {
  ValidatedInput,
  ValidatedDatePicker,
} from "../../validation/ValidatedFields";
import { validate as validateEngine } from "../../validation/engine";

interface UserFormProps {
  isOpen: boolean;
  user?: User | null;
  /** Fires after a successful save/status change. The optional toast is shown
      by the parent (which stays mounted after the modal closes). */
  onSave: (toast?: ToastMessage) => void;
  onClose: () => void;
  /** Fires only after creating a NEW user (not on edit), with the new user's id. */
  onUserCreated?: (newUserId: number) => void | Promise<void>;
  canModify?: boolean;
  canApprove?: boolean;
  modifyDeniedMessage?: string;
  approveDeniedMessage?: string;
  /** Render grounded as an in-flow page instead of a Modal popup.
      Used by the Identity page; defaults to false (modal) everywhere else. */
  asPage?: boolean;
}

interface FormData {
  first_name: string;
  last_name: string;
  father_name: string;
  login_id: string;
  email: string;
  dob: string;
  gender: string;
  phone: string;
  pan_number: string;
  aadhar_number: string;
  ssn_number: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  password: string;
  confirmPassword: string;
}

interface QualificationRow {
  degree_name: string;
  institution_name: string;
  period_start: string;
  period_end: string;
  grade_or_marks: string;
  course_type: string;
}

interface ExperienceRow {
  company_name: string;
  position_title: string;
  period_from: string;
  period_to: string;
  job_profile_desc: string;
}

const EMPTY_FORM: FormData = {
  first_name: "",
  last_name: "",
  father_name: "",
  login_id: "",
  email: "",
  dob: "",
  gender: "",
  phone: "",
  pan_number: "",
  aadhar_number: "",
  ssn_number: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  country: "",
  pincode: "",
  password: "",
  confirmPassword: "",
};

const EMPTY_QUAL: QualificationRow = {
  degree_name: "",
  institution_name: "",
  period_start: "",
  period_end: "",
  grade_or_marks: "",
  course_type: "",
};

const EMPTY_EXP: ExperienceRow = {
  company_name: "",
  position_title: "",
  period_from: "",
  period_to: "",
  job_profile_desc: "",
};

const UserForm: React.FC<UserFormProps> = ({
  isOpen,
  user,
  onSave,
  onClose,
  onUserCreated,
  canModify = true,
  canApprove = true,
  modifyDeniedMessage = "You do not have permission to modify users.",
  approveDeniedMessage = "You do not have permission to approve users.",
  asPage = false,
}) => {
  const isEditMode = Boolean(user);
  // Approved records are locked: modal becomes pure read-only until the
  // workflow dot is reopened to draft. Re-enables editing once non-approved.
  const isApproved = isEditMode && (user as any)?.workflow_state === "approved";
  // Entity Super Admins are viewable but never editable here — they are
  // managed in the Super Admin portal. Force read-only like the approved lock.
  const isEntityAdmin =
    isEditMode &&
    ((user as any)?.is_entity_admin === 1 ||
      (user as any)?.is_entity_admin === true);
  // View-first edit: when editing existing record, start in view mode
  const [isEditing, setIsEditing] = useState(false);
  const readOnly = !canModify || isApproved || isEntityAdmin || (isEditMode && !isEditing);
  const { allowed: canModifyRbac, loading: permLoading } = usePermission('ORG_RBAC', 'view+modify');
  const { formatDate } = useEntitySettings();
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [checkingLoginId, setCheckingLoginId] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [formData, setFormData] = useState<FormData>({ ...EMPTY_FORM });
  // Single error map for the main form, backed by the shared validation engine.
  const fieldVh = useFieldValidation();
  const errors = fieldVh.errors;
  // Each sub-modal gets its own validation instance.
  const qualVh = useFieldValidation();
  const expVh = useFieldValidation();
  const [qualifications, setQualifications] = useState<QualificationRow[]>([]);
  const [experiences, setExperiences] = useState<ExperienceRow[]>([]);
  const [userPostings, setUserPostings] = useState<Posting[]>([]);
  const [postingsLoading, setPostingsLoading] = useState(false);
  const [userRbac, setUserRbac] = useState<UserRbacRole[]>([]);
  const [rbacLoading, setRbacLoading] = useState(false);
  // Inline role-assign form
  const [showAssignRole, setShowAssignRole] = useState(false);
  const [allRoles, setAllRoles] = useState<AdminRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState('');
  const [effectiveDateToAdd, setEffectiveDateToAdd] = useState('');
  const [notesToAdd, setNotesToAdd] = useState('');
  const [assigning, setAssigning] = useState(false);
  // Inline role-remove popconfirm
  const [removingRoleId, setRemovingRoleId] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);
  // ACL rows are also rendered (and self-refetched) by UserAclPanel; this copy
  // exists purely to drive the ACL tab's count badge, mirroring RBAC/Positions.
  const [userAcl, setUserAcl] = useState<UserAclRow[]>([]);
  const [activeTab, setActiveTab] = useState<
    | "basic"
    | "address"
    | "identity"
    | "education"
    | "experience"
    | "positions"
    | "sops"
    | "rbac"
    | "acl"
  >("basic");
  const [qualModalOpen, setQualModalOpen] = useState(false);
  const [editingQualIdx, setEditingQualIdx] = useState<number | null>(null);
  const [qualDraft, setQualDraft] = useState<QualificationRow>(EMPTY_QUAL);
  const [qualIsViewMode, setQualIsViewMode] = useState(false);
  const [expModalOpen, setExpModalOpen] = useState(false);
  const [editingExpIdx, setEditingExpIdx] = useState<number | null>(null);
  const [expDraft, setExpDraft] = useState<ExperienceRow>(EMPTY_EXP);
  const [expIsViewMode, setExpIsViewMode] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        father_name: user.father_name || "",
        login_id: user.login_id || "",
        email: user.email || "",
        dob: user.dob ? String(user.dob).split("T")[0] : "",
        gender: user.gender || "",
        phone: user.phone || "",
        pan_number: user.pan_number || "",
        aadhar_number: user.aadhar_number || "",
        ssn_number: user.ssn_number || "",
        address_line1: user.address_line1 || "",
        address_line2: user.address_line2 || "",
        city: user.city || "",
        state: user.state || "",
        country: user.country || "",
        pincode: user.pincode || "",
        password: "",
        confirmPassword: "",
      });
      loadChildRecords(user.id);
      loadUserPostings(user.id);
      loadUserRbac(user.id);
      loadUserAcl(user.id);
      loadAllRoles();
      setIsEditing(false);
    } else {
      setFormData({ ...EMPTY_FORM });
      setQualifications([]);
      setExperiences([]);
      setUserPostings([]);
      setUserRbac([]);
      setUserAcl([]);
      setAllRoles([]);
      setShowAssignRole(false);
      setSelectedRoleToAdd('');
      setEffectiveDateToAdd('');
      setNotesToAdd('');
      setRemovingRoleId(null);
      setIsEditing(false);
    }
    fieldVh.reset();
    setActiveTab("basic");
    setToggling(false);
  }, [user, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleActive = async () => {
    if (!user) return;
    if (isApproved) {
      setToast({ type: 'warning', message: "Approved records are locked. Reopen to draft first." });
      return;
    }
    if (!canApprove) {
      setToast({ type: 'warning', message: approveDeniedMessage });
      return;
    }
    const next = user.is_active ? 0 : 1;
    const confirmFn = user.is_active ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this user?" : "Inactivate this user?",
      text: next
        ? "The user will appear in active lists again."
        : "The user will be hidden from active lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    setToggling(true);
    try {
      await userService.updateUser(user.id, { is_active: next } as any);
      // Toast is raised by the parent — this modal unmounts on onSave().
      onSave({
        type: next ? 'success' : 'warning',
        message: next ? 'User reactivated.' : 'User inactivated.',
      });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || "Failed to update status" });
    } finally {
      setToggling(false);
    }
  };

  const loadChildRecords = async (userId: number) => {
    try {
      const [quals, exps] = await Promise.all([
        userService.getQualificationsByUser(userId),
        userService.getExperiencesByUser(userId),
      ]);
      console.log(
        "[UserForm] Loaded qualifications:",
        quals.length,
        "experiences:",
        exps.length,
      );
      setQualifications(
        quals.map((q: any) => ({
          degree_name: q.degree_name || "",
          institution_name: q.institution_name || "",
          period_start: q.period_start
            ? String(q.period_start).split("T")[0]
            : "",
          period_end: q.period_end ? String(q.period_end).split("T")[0] : "",
          grade_or_marks: q.grade_or_marks || "",
          course_type: q.course_type || "",
        })),
      );
      setExperiences(
        exps.map((e: any) => ({
          company_name: e.company_name || "",
          position_title: e.position_title || "",
          period_from: e.period_from ? String(e.period_from).split("T")[0] : "",
          period_to: e.period_to ? String(e.period_to).split("T")[0] : "",
          job_profile_desc: e.job_profile_desc || "",
        })),
      );
    } catch (err) {
      console.error("[UserForm] Failed to load child records:", err);
    }
  };

  const loadUserRbac = async (userId: number) => {
    setRbacLoading(true);
    try {
      const data = await rolesService.getUserRbac(userId);
      setUserRbac(data);
    } catch (err) {
      console.error("[UserForm] Failed to load user RBAC:", err);
      setUserRbac([]);
    } finally {
      setRbacLoading(false);
    }
  };

  const loadAllRoles = async () => {
    setRolesLoading(true);
    try {
      const { data } = await rolesService.getRoles({ limit: 500 });
      setAllRoles(data);
    } catch (err) {
      console.error('[UserForm] Failed to load roles:', err);
      setAllRoles([]);
    } finally {
      setRolesLoading(false);
    }
  };

  const handleAssignRole = async () => {
    if (!user || !selectedRoleToAdd) return;
    setAssigning(true);
    try {
      await rolesService.assignUserToRole(
        Number(selectedRoleToAdd),
        user.id,
        effectiveDateToAdd || null,
        notesToAdd || null,
      );
      setShowAssignRole(false);
      setSelectedRoleToAdd('');
      setEffectiveDateToAdd('');
      setNotesToAdd('');
      await loadUserRbac(user.id);
    } catch (err) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to assign role') });
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveRole = async (roleId: number) => {
    if (!user) return;
    setRemoving(true);
    try {
      await rolesService.deactivateUserFromRole(roleId, user.id);
      setRemovingRoleId(null);
      await loadUserRbac(user.id);
    } catch (err) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to remove role') });
    } finally {
      setRemoving(false);
    }
  };

  const loadUserAcl = async (userId: number) => {
    try {
      // Active (non-revoked) grants only — matches UserAclPanel's default view.
      setUserAcl(await itgcService.listUserAcl(userId, false));
    } catch (err) {
      console.error("[UserForm] Failed to load user ACL count:", err);
      setUserAcl([]);
    }
  };

  const loadUserPostings = async (userId: number) => {
    setPostingsLoading(true);
    try {
      const data = await postingService.getByUser(userId);
      // Sort: current (no end_date) first, then by start_date descending
      const sorted = [...data].sort((a, b) => {
        if (!a.end_date && b.end_date) return -1;
        if (a.end_date && !b.end_date) return 1;
        return (b.start_date || "").localeCompare(a.start_date || "");
      });
      setUserPostings(sorted);
    } catch (err) {
      console.error("[UserForm] Failed to load user postings:", err);
      setUserPostings([]);
    } finally {
      setPostingsLoading(false);
    }
  };

  // Used by the non-engine fields (login_id, password, confirmPassword). The
  // engine-coded fields go through Validated* wrappers, which clear their own
  // error on change and run the shared engine on blur.
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name } = e.target;
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) fieldVh.clearError(name);
  };

  const handleCopyOnDoubleClick = async (e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const text = target.value;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      target.select();
      try { document.execCommand('copy'); } catch {}
    }
  };

  const checkLoginIdUnique = async () => {
    const login = formData.login_id.trim();
    if (!login) return;
    const loginVal = validateUserInput.validateUserId(login);
    if (!loginVal.isValid) return;
    setCheckingLoginId(true);
    try {
      const exists = await userService.checkLoginIdExists(
        login,
        isEditMode && user ? user.id : undefined,
      );
      if (exists) {
        fieldVh.setError("login_id", `Login ID '${login}' already taken`);
      } else {
        fieldVh.clearError("login_id");
      }
    } catch {
      // surfaces on save catch
    } finally {
      setCheckingLoginId(false);
    }
  };

  const basicFields = [
    "first_name",
    "last_name",
    "login_id",
    "email",
    "phone",
    "password",
    "confirmPassword",
  ];

  const validateForm = (): boolean => {
    // Track failing field names locally so the tab auto-switch is correct
    // immediately (React state from validateAll/setError lands asynchronously).
    const failed: string[] = [];

    // Format validation via the shared engine. Email is required on create, and
    // on edit (the existing rule treated email as required in both modes).
    const engineSpecs = [
      { name: "first_name", code: 109, value: formData.first_name, required: true },
      { name: "last_name", code: 109, value: formData.last_name, required: true },
      { name: "father_name", code: 109, value: formData.father_name },
      { name: "email", code: 401, value: formData.email, required: true },
      { name: "phone", code: 405, value: formData.phone },
      { name: "dob", code: 305, value: formData.dob },
      { name: "pan_number", code: 510, value: formData.pan_number },
      { name: "address_line1", code: 110, value: formData.address_line1 },
      { name: "address_line2", code: 110, value: formData.address_line2 },
      { name: "city", code: 109, value: formData.city },
      { name: "state", code: 109, value: formData.state },
      { name: "country", code: 109, value: formData.country },
    ];
    const engineOk = fieldVh.validateAll(engineSpecs);
    if (!engineOk) {
      // Re-derive which fields failed (validateFields logic) for the tab switch.
      for (const s of engineSpecs) {
        const isEmpty =
          s.value == null || (typeof s.value === "string" && s.value.trim() === "");
        if (isEmpty && !s.required) continue;
        if (isEmpty && s.required) { failed.push(s.name); continue; }
        const r = validateEngine(s.code, s.value);
        if (!r.valid) failed.push(s.name);
      }
    }

    // Preserved custom checks that the engine does not cover.
    let customOk = true;

    // login_id — create only (3-20 alnum/underscore), kept off the engine.
    if (!isEditMode) {
      const loginVal = validateUserInput.validateUserId(formData.login_id);
      if (!loginVal.isValid) {
        fieldVh.setError("login_id", loginVal.error || "Invalid");
        failed.push("login_id");
        customOk = false;
      }
    }

    // dob future-date guard (preserved exactly).
    if (formData.dob) {
      const today = new Date().toISOString().split("T")[0];
      if (formData.dob > today) {
        fieldVh.setError("dob", "Date of birth cannot be in the future");
        failed.push("dob");
        customOk = false;
      }
    }

    // Password — security credential, kept on validateUserInput + match check.
    if (!isEditMode || formData.password) {
      const passVal = validateUserInput.validatePassword(formData.password);
      if (!passVal.isValid) {
        fieldVh.setError("password", passVal.error || "Invalid");
        failed.push("password");
        customOk = false;
      }
      if (formData.password !== formData.confirmPassword) {
        fieldVh.setError("confirmPassword", "Passwords do not match");
        failed.push("confirmPassword");
        customOk = false;
      }
    }

    const ok = engineOk && customOk;
    // Auto-switch to the basic tab when an error lives there.
    if (!ok) {
      const hasBasicError = failed.some((k) => basicFields.includes(k));
      // dob lives on the basic tab too but is not in basicFields.
      if (hasBasicError || failed.includes("dob")) setActiveTab("basic");
    }
    return ok;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (readOnly) {
      setToast({ type: 'warning', message: modifyDeniedMessage });
      return;
    }
    if (!validateForm()) return;

    setLoading(true);
    fieldVh.reset();

    try {
      let savedUserId: number;

      if (isEditMode && user) {
        const updateData: UpdateUserRequest = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          father_name: formData.father_name.trim() || undefined,
          email: formData.email.trim(),
          dob: formData.dob || undefined,
          gender: formData.gender || undefined,
          phone: formData.phone.trim() || undefined,
          pan_number: formData.pan_number.trim() || undefined,
          aadhar_number: formData.aadhar_number.trim() || undefined,
          ssn_number: formData.ssn_number.trim() || undefined,
          address_line1: formData.address_line1.trim() || undefined,
          address_line2: formData.address_line2.trim() || undefined,
          city: formData.city.trim() || undefined,
          state: formData.state.trim() || undefined,
          country: formData.country.trim() || undefined,
          pincode: formData.pincode.trim() || undefined,
        };
        // Only send password when the admin typed one (blank = keep current).
        if (formData.password) {
          updateData.password = formData.password;
        }
        await userService.updateUser(user.id, updateData);
        savedUserId = user.id;
      } else {
        const createData: CreateUserRequest = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          father_name: formData.father_name.trim() || undefined,
          login_id: formData.login_id.trim(),
          email: formData.email.trim(),
          password: formData.password,
          dob: formData.dob || undefined,
          gender: formData.gender || undefined,
          phone: formData.phone.trim() || undefined,
          pan_number: formData.pan_number.trim() || undefined,
          aadhar_number: formData.aadhar_number.trim() || undefined,
          ssn_number: formData.ssn_number.trim() || undefined,
          address_line1: formData.address_line1.trim() || undefined,
          address_line2: formData.address_line2.trim() || undefined,
          city: formData.city.trim() || undefined,
          state: formData.state.trim() || undefined,
          country: formData.country.trim() || undefined,
          pincode: formData.pincode.trim() || undefined,
        };
        const newUser = await userService.createUser(createData);
        savedUserId = newUser.id;
        if (onUserCreated) {
          await onUserCreated(savedUserId);
        }
      }

      // Save qualifications & experiences
      // Reconcile by soft-deleting all existing rows for this user (edit mode),
      // then re-creating from the current modal state — including the empty case.
      const validQuals = qualifications.filter(
        (q) => q.degree_name && q.institution_name,
      );
      const validExps = experiences.filter(
        (ex) => ex.company_name && ex.position_title && ex.period_from,
      );

      if (isEditMode) {
        await Promise.all([
          userService.deleteQualificationsByUser(savedUserId).catch(() => {}),
          userService.deleteExperiencesByUser(savedUserId).catch(() => {}),
        ]);
      }

      for (const q of validQuals) {
        await userService.createQualification({
          user_id: savedUserId,
          degree_name: q.degree_name,
          institution_name: q.institution_name,
          period_start: q.period_start || null,
          period_end: q.period_end || null,
          grade_or_marks: q.grade_or_marks || null,
          course_type: q.course_type || null,
        });
      }
      for (const ex of validExps) {
        await userService.createExperience({
          user_id: savedUserId,
          company_name: ex.company_name,
          position_title: ex.position_title,
          period_from: ex.period_from || null,
          period_to: ex.period_to || null,
          job_profile_desc: ex.job_profile_desc || null,
        });
      }

      setIsEditing(false);
      onSave();
    } catch (error: any) {
      const msg = extractApiError(error, "Failed to save user");
      if (/login.?id|user.?id|username/i.test(msg) && /exist|duplicate|unique|taken|registered/i.test(msg)) {
        fieldVh.setError("login_id", msg);
        setActiveTab("basic");
      } else if (/email/i.test(msg) && /exist|duplicate|unique|taken|registered/i.test(msg)) {
        fieldVh.setError("email", msg);
        setActiveTab("basic");
      } else {
        setToast({ type: 'error', message: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  // Qualification helpers
  const removeQualification = (idx: number) =>
    setQualifications((prev) => prev.filter((_, i) => i !== idx));
  const openCreateQualModal = () => {
    setEditingQualIdx(null);
    setQualDraft({ ...EMPTY_QUAL });
    qualVh.reset();
    setQualModalOpen(true);
  };
  const openEditQualModal = (idx: number) => {
    setEditingQualIdx(idx);
    setQualDraft({ ...qualifications[idx] });
    qualVh.reset();
    setQualIsViewMode(true);
    setQualModalOpen(true);
  };
  const closeQualModal = () => {
    setQualModalOpen(false);
    setEditingQualIdx(null);
    setQualIsViewMode(false);
  };
  const saveQualModal = () => {
    const ok = qualVh.validateAll([
      { name: "degree_name", code: 109, value: qualDraft.degree_name },
      { name: "institution_name", code: 109, value: qualDraft.institution_name },
      { name: "grade_or_marks", code: 109, value: qualDraft.grade_or_marks },
      { name: "period_start", code: 305, value: qualDraft.period_start },
      { name: "period_end", code: 305, value: qualDraft.period_end },
      ...(qualDraft.period_start && qualDraft.period_end
        ? [{
            name: "period_end", code: 601, value: qualDraft.period_end,
            additional: { from_date: qualDraft.period_start, to_date: qualDraft.period_end },
          }]
        : []),
    ]);
    if (!ok) return;
    if (editingQualIdx === null) {
      setQualifications((prev) => [...prev, qualDraft]);
    } else {
      setQualifications((prev) =>
        prev.map((r, i) => (i === editingQualIdx ? qualDraft : r)),
      );
    }
    closeQualModal();
  };
  const updateQualDraft = (field: keyof QualificationRow, value: string) => {
    setQualDraft((prev) => ({ ...prev, [field]: value }));
  };

  // Experience helpers
  const removeExperience = (idx: number) =>
    setExperiences((prev) => prev.filter((_, i) => i !== idx));
  const openCreateExpModal = () => {
    setEditingExpIdx(null);
    setExpDraft({ ...EMPTY_EXP });
    expVh.reset();
    setExpModalOpen(true);
  };
  const openEditExpModal = (idx: number) => {
    setEditingExpIdx(idx);
    setExpDraft({ ...experiences[idx] });
    expVh.reset();
    setExpIsViewMode(true);
    setExpModalOpen(true);
  };
  const closeExpModal = () => {
    setExpModalOpen(false);
    setEditingExpIdx(null);
    setExpIsViewMode(false);
  };
  const saveExpModal = () => {
    const ok = expVh.validateAll([
      { name: "company_name", code: 109, value: expDraft.company_name },
      { name: "position_title", code: 109, value: expDraft.position_title },
      { name: "job_profile_desc", code: 110, value: expDraft.job_profile_desc },
      { name: "period_from", code: 305, value: expDraft.period_from },
      { name: "period_to", code: 305, value: expDraft.period_to },
      ...(expDraft.period_from && expDraft.period_to
        ? [{
            name: "period_to", code: 601, value: expDraft.period_to,
            additional: { from_date: expDraft.period_from, to_date: expDraft.period_to },
          }]
        : []),
    ]);
    if (!ok) return;
    if (editingExpIdx === null) {
      setExperiences((prev) => [...prev, expDraft]);
    } else {
      setExperiences((prev) =>
        prev.map((r, i) => (i === editingExpIdx ? expDraft : r)),
      );
    }
    closeExpModal();
  };
  const updateExpDraft = (field: keyof ExperienceRow, value: string) => {
    setExpDraft((prev) => ({ ...prev, [field]: value }));
  };

  const tabs: {
    key: typeof activeTab;
    label: string;
    icon: React.ElementType;
    count?: number;
  }[] = [
    { key: "basic", label: "Basic Info", icon: UserIcon },
    { key: "address", label: "Address", icon: Home },
    { key: "identity", label: "Identity", icon: CreditCard },
    {
      key: "education",
      label: "Education",
      icon: GraduationCap,
      count: qualifications.length || undefined,
    },
    {
      key: "experience",
      label: "Experience",
      icon: Briefcase,
      count: experiences.length || undefined,
    },
    ...(isEditMode
      ? [
          {
            key: "positions" as typeof activeTab,
            label: "Positions",
            icon: Building2,
            count: userPostings.length || undefined,
          },
        ]
      : []),
    // My SOPs only on the Identity page (asPage) — it lists the LOGGED-IN
    // user's SOPs, which would be wrong inside the org admin edit modal.
    ...(isEditMode && asPage
      ? [
          {
            key: "sops" as typeof activeTab,
            label: "My SOPs",
            icon: ClipboardList,
          },
        ]
      : []),
    ...(isEditMode
      ? [
          {
            key: "rbac" as typeof activeTab,
            label: "RBAC",
            icon: Shield,
            count: userRbac.length || undefined,
          },
        ]
      : []),
    ...(isEditMode
      ? [
          {
            key: "acl" as typeof activeTab,
            label: "ACL",
            icon: Key,
            count: userAcl.length || undefined,
          },
        ]
      : []),
  ];

  // Styling handled by <Input>/<Select>/<Textarea> components

  const titleNode = isEditMode ? (
    <>
      {isEditing ? "Editing" : "Viewing"} "
      <span className="item-highlight">
        {[user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
          "User"}
        {user?.login_id ? ` (${user.login_id})` : ""}
      </span>
      "
    </>
  ) : (
    "Create User"
  );

  // Body + footer are identical; only the outer shell differs (page vs modal).
  // Vertical tab rail (left) + content (right) — mirrors the Action Centre /
  // History work-shell so the profile reads like the other pages.
  const bodyContent = (
          <div
            className={
              asPage
                ? "flex h-full overflow-hidden"
                : "flex overflow-hidden"
            }
          >
            {/* ── Left nav card ─────────────────────────────────────── */}
            <div
              className="netbody-card side-nav-card"
              style={
                asPage
                  ? { height: "100%", position: "sticky", top: 0 }
                  : { height: "auto", maxHeight: "100%", position: "sticky", top: 0 }
              }
            >
              <div className="side-nav-card-heading">Profile</div>
              <nav role="tablist" aria-label="Profile sections">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === t.key}
                    onClick={() => setActiveTab(t.key)}
                    className={`side-panel-menu-item${activeTab === t.key ? " side-panel-menu-item--active" : ""}`}
                    data-testid={`TAB_user_form_${t.key}`}
                  >
                    <t.icon size={14} />
                    <span className="flex-1">{t.label}</span>
                    {t.count ? (
                      <span className="app-modal-tab-badge">{t.count}</span>
                    ) : null}
                  </button>
                ))}
              </nav>
            </div>

            {/* ── Right content card: toolbar-less, holds the form ──── */}
            <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">
              <div className="netbody-content">
              {activeTab === "sops" ? (
                // My SOPs catalogue — moved here from the Action Centre.
                // Rendered OUTSIDE the form/fieldset so Initiate stays enabled
                // even though the profile fields are read-only.
                <div
                  className="flex flex-col"
                  style={{ height: "calc(100vh - 240px)", minHeight: 360 }}
                  data-testid="PANEL_identity_sops"
                >
                  <MySopsTab view="catalogue" scope="active" readOnly={false} />
                </div>
              ) : (
              <form
                id="user-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-y-4"
                data-testid="MOD_user_form"
              >
            {/* display:contents keeps the flex layout while disabling every
                native input/button inside when the record is read-only. */}
            <fieldset
              disabled={readOnly}
              className="contents"
              data-testid="FS_user_form_lock"
            >
            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ValidatedInput
                    vh={fieldVh}
                    name="first_name"
                    code={109}
                    required
                    label="First Name"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, first_name: e.target.value }))
                    }
                    disabled={loading}
                    data-testid="INP_user_first_name"
                  />
                  <ValidatedInput
                    vh={fieldVh}
                    name="last_name"
                    code={109}
                    required
                    label="Last Name"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, last_name: e.target.value }))
                    }
                    disabled={loading}
                    data-testid="INP_user_last_name"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ValidatedInput
                    vh={fieldVh}
                    name="father_name"
                    code={109}
                    label="Father's Name"
                    value={formData.father_name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, father_name: e.target.value }))
                    }
                    disabled={loading}
                    data-testid="INP_user_father_name"
                  />
                  <FormControl.Input
                    label="Login ID"
                    required={!isEditMode}
                    name="login_id"
                    value={formData.login_id}
                    onChange={handleChange}
                    onBlur={checkLoginIdUnique}
                    onDoubleClick={handleCopyOnDoubleClick}
                    disabled={loading || isEditMode}
                    error={errors.login_id}
                    autoComplete="off"
                    helper={
                      !isEditMode
                        ? "3-20 chars, alphanumeric + underscore"
                        : undefined
                    }
                    data-testid="INP_user_login_id"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormControl.Input
                    label="Email"
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, email: e.target.value }));
                      fieldVh.clearError("email");
                    }}
                    onBlur={(e) =>
                      fieldVh.validateField("email", 401, e.target.value, { required: true })
                    }
                    onDoubleClick={handleCopyOnDoubleClick}
                    disabled={loading}
                    error={errors.email}
                    autoComplete="off"
                    data-testid="INP_user_email"
                  />
                  <FormControl.Input
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        phone: e.target.value.replace(/[^\d+\s-]/g, "").slice(0, 15),
                      }));
                      fieldVh.clearError("phone");
                    }}
                    onBlur={(e) =>
                      fieldVh.validateField("phone", 405, e.target.value)
                    }
                    onDoubleClick={handleCopyOnDoubleClick}
                    disabled={loading}
                    error={errors.phone}
                    placeholder="+91XXXXXXXXXX or 10-digit"
                    maxLength={15}
                    data-testid="INP_user_phone"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormControl.DatePicker
                    label="Date of Birth"
                    name="dob"
                    value={formData.dob}
                    maxDate={new Date()}
                    onChange={(e: any) => {
                      const v = e.target.value;
                      const today = new Date().toISOString().split('T')[0];
                      if (v && v > today) {
                        fieldVh.setError('dob', 'Date of birth cannot be in the future');
                        return;
                      }
                      setFormData((prev) => ({ ...prev, dob: v }));
                      fieldVh.clearError('dob');
                    }}
                    onBlur={() => {
                      const today = new Date().toISOString().split('T')[0];
                      if (formData.dob && formData.dob > today) {
                        fieldVh.setError('dob', 'Date of birth cannot be in the future');
                        return;
                      }
                      fieldVh.validateField('dob', 305, formData.dob);
                    }}
                    disabled={loading}
                    error={errors.dob}
                    data-testid="INP_user_dob"
                  />
                  <FormControl.SearchSelect
                    label="Gender"
                    name="gender"
                    value={formData.gender}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        gender: value ? String(value) : "",
                      }))
                    }
                    options={[
                      { value: "Male", label: "Male" },
                      { value: "Female", label: "Female" },
                      { value: "Other", label: "Other" },
                    ]}
                    placeholder="Select"
                    clearable
                    disabled={loading}
                    data-testid="SEL_user_gender"
                  />
                </div>
                <div className="border-t border-slate-200 pt-4 mt-2">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">
                    Security{" "}
                    {isEditMode && (
                      <span className="text-xs text-slate-400 font-normal">
                        (leave blank to keep current password)
                      </span>
                    )}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormControl.Input
                      label={isEditMode ? "New Password" : "Password"}
                      required={!isEditMode}
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                      error={errors.password}
                      autoComplete="new-password"
                      placeholder={
                        isEditMode
                          ? "Leave empty to keep current"
                          : "Enter password"
                      }
                      data-testid="INP_user_password"
                    />
                    {(!isEditMode || formData.password) && (
                      <FormControl.Input
                        label="Confirm Password"
                        required
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        disabled={loading}
                        error={errors.confirmPassword}
                        autoComplete="new-password"
                        placeholder="Re-enter password"
                        data-testid="INP_user_confirm_password"
                      />
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Address Tab */}
            {activeTab === "address" && (
              <>
                <ValidatedInput
                  vh={fieldVh}
                  name="address_line1"
                  code={110}
                  label="Address Line 1"
                  value={formData.address_line1}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, address_line1: e.target.value }))
                  }
                  disabled={loading}
                  data-testid="INP_user_address_line1"
                />
                <ValidatedInput
                  vh={fieldVh}
                  name="address_line2"
                  code={110}
                  label="Address Line 2"
                  value={formData.address_line2}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, address_line2: e.target.value }))
                  }
                  disabled={loading}
                  data-testid="INP_user_address_line2"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ValidatedInput
                    vh={fieldVh}
                    name="city"
                    code={109}
                    label="City"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, city: e.target.value }))
                    }
                    disabled={loading}
                    data-testid="INP_user_city"
                  />
                  <ValidatedInput
                    vh={fieldVh}
                    name="state"
                    code={109}
                    label="State"
                    value={formData.state}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, state: e.target.value }))
                    }
                    disabled={loading}
                    data-testid="INP_user_state"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ValidatedInput
                    vh={fieldVh}
                    name="country"
                    code={109}
                    label="Country"
                    value={formData.country}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, country: e.target.value }))
                    }
                    disabled={loading}
                    data-testid="INP_user_country"
                  />
                  <FormControl.Input
                    label="Pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    disabled={loading}
                    data-testid="INP_user_pincode"
                  />
                </div>
              </>
            )}

            {/* Identity Tab */}
            {activeTab === "identity" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormControl.Input
                    label="PAN Number"
                    name="pan_number"
                    value={formData.pan_number}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, pan_number: e.target.value }));
                      fieldVh.clearError("pan_number");
                    }}
                    onBlur={(e) =>
                      fieldVh.validateField("pan_number", 510, e.target.value)
                    }
                    onDoubleClick={handleCopyOnDoubleClick}
                    disabled={loading}
                    error={errors.pan_number}
                    maxLength={10}
                    data-testid="INP_user_pan"
                  />
                  <FormControl.Input
                    label="Aadhar Number"
                    name="aadhar_number"
                    value={formData.aadhar_number}
                    onChange={handleChange}
                    onDoubleClick={handleCopyOnDoubleClick}
                    disabled={loading}
                    maxLength={12}
                    data-testid="INP_user_aadhar"
                  />
                </div>
                <FormControl.Input
                  label="SSN Number"
                  name="ssn_number"
                  value={formData.ssn_number}
                  onChange={handleChange}
                  onDoubleClick={handleCopyOnDoubleClick}
                  disabled={loading}
                  maxLength={20}
                  data-testid="INP_user_ssn"
                />
              </>
            )}

            {/* Education Tab */}
            {activeTab === "education" && (
              <div className="flex flex-col gap-y-2">
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="fill"
                    icon={Plus}
                    onClick={openCreateQualModal}
                    data-testid="BTN_user_qual_create"
                  >
                    Create
                  </Button>
                </div>
                <div className="table-list">
                  <div
                    className="table-list-scroll"
                    style={{ minHeight: "auto" }}
                  >
                    <table className="table-list-table">
                      <thead>
                        <tr>
                          <th className="table-list-th table-list-cell--sno">
                            S.No
                          </th>
                          <th className="table-list-th">Degree</th>
                          <th className="table-list-th">Institution</th>
                          <th className="table-list-th">Period</th>
                          <th className="table-list-th">Grade / Marks</th>
                          <th className="table-list-th table-list-th--right">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {qualifications.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="table-list-cell">
                              <NoDataFound
                                title="No Qualifications"
                                message='No qualifications added. Click "Create" to add one.'
                              />
                            </td>
                          </tr>
                        ) : (
                          qualifications.map((q, idx) => (
                            <tr
                              key={idx}
                              className="table-list-row"
                              onClick={() => openEditQualModal(idx)}
                              data-testid={`BTN_user_qual_row_${idx}_open`}
                            >
                              <td className="table-list-cell table-list-cell--sno">
                                {idx + 1}
                              </td>
                              <td className="table-list-cell">
                                <span className="table-list-cell-title">
                                  {q.degree_name || "-"}
                                </span>
                              </td>
                              <td className="table-list-cell">
                                {q.institution_name || "-"}
                              </td>
                              <td className="table-list-cell">
                                {formatDate(q.period_start)} –{" "}
                                {formatDate(q.period_end)}
                              </td>
                              <td className="table-list-cell">
                                {q.grade_or_marks || "-"}
                              </td>
                              <td className="table-list-cell table-list-cell--actions">
                                <button
                                  type="button"
                                  className="table-list-action table-list-action--delete"
                                  title="Delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeQualification(idx);
                                  }}
                                  data-testid={`BTN_user_qual_row_${idx}_delete`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Experience Tab */}
            {activeTab === "experience" && (
              <div className="flex flex-col gap-y-2">
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="fill"
                    icon={Plus}
                    onClick={openCreateExpModal}
                    data-testid="BTN_user_exp_create"
                  >
                    Create
                  </Button>
                </div>
                <div className="table-list">
                  <div
                    className="table-list-scroll"
                    style={{ minHeight: "auto" }}
                  >
                    <table className="table-list-table">
                      <thead>
                        <tr>
                          <th className="table-list-th table-list-cell--sno">
                            S.No
                          </th>
                          <th className="table-list-th">Company</th>
                          <th className="table-list-th">Position</th>
                          <th className="table-list-th">Period</th>
                          <th className="table-list-th">Description</th>
                          <th className="table-list-th table-list-th--right">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {experiences.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="table-list-cell">
                              <NoDataFound
                                title="No Experience"
                                message='No experience added. Click "Create" to add one.'
                              />
                            </td>
                          </tr>
                        ) : (
                          experiences.map((ex, idx) => (
                            <tr
                              key={idx}
                              className="table-list-row"
                              onClick={() => openEditExpModal(idx)}
                              data-testid={`BTN_user_exp_row_${idx}_open`}
                            >
                              <td className="table-list-cell table-list-cell--sno">
                                {idx + 1}
                              </td>
                              <td className="table-list-cell">
                                <span className="table-list-cell-title">
                                  {ex.company_name || "-"}
                                </span>
                              </td>
                              <td className="table-list-cell">
                                {ex.position_title || "-"}
                              </td>
                              <td className="table-list-cell">
                                {formatDate(ex.period_from)} –{" "}
                                {ex.period_to
                                  ? formatDate(ex.period_to)
                                  : "Present"}
                              </td>
                              <td className="table-list-cell table-list-cell--truncate">
                                {ex.job_profile_desc || "-"}
                              </td>
                              <td className="table-list-cell table-list-cell--actions">
                                <button
                                  type="button"
                                  className="table-list-action table-list-action--delete"
                                  title="Delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeExperience(idx);
                                  }}
                                  data-testid={`BTN_user_exp_row_${idx}_delete`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Positions Tab (read-only, edit mode only) */}
            {activeTab === "positions" && (
              <>
                {postingsLoading ? (
                  <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
                    Loading positions...
                  </div>
                ) : userPostings.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm">
                    <MapPin className="w-8 h-8 mb-2 opacity-30" />
                    <span>No positions assigned to this user.</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {userPostings.map((p) => {
                      const isCurrent = !p.end_date;
                      return (
                        <div
                          key={p.id}
                          className={`border rounded-lg p-3 ${isCurrent ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <MapPin
                                className={`w-4 h-4 ${isCurrent ? "text-blue-600" : "text-slate-400"}`}
                              />
                              <span
                                className={`font-medium text-sm ${isCurrent ? "text-blue-900" : "text-slate-700"}`}
                              >
                                {p.position_name || "-"}
                              </span>
                              {p.position_code && (
                                <span className="text-xs text-slate-400">
                                  ({p.position_code})
                                </span>
                              )}
                            </div>
                            <span className={activeBadgeCss(isCurrent ? 1 : 0)}>
                              {isCurrent ? "Current" : "Past"}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                            {p.authority_name && (
                              <span>
                                Authority:{" "}
                                <span className="text-slate-700">
                                  {p.authority_name}
                                </span>
                              </span>
                            )}
                            {p.location_name && (
                              <span>
                                Location:{" "}
                                <span className="text-slate-700">
                                  {p.location_name}
                                </span>
                              </span>
                            )}
                            {p.posting_type && (
                              <span>
                                Type:{" "}
                                <span className="text-slate-700">
                                  {p.posting_type}
                                </span>
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex gap-x-4 text-xs text-slate-500">
                            <span>
                              From:{" "}
                              <span className="text-slate-700">
                                {p.start_date ? formatDate(p.start_date) : "-"}
                              </span>
                            </span>
                            <span>
                              To:{" "}
                              <span className="text-slate-700">
                                {p.end_date ? formatDate(p.end_date) : "Present"}
                              </span>
                            </span>
                            {p.reference_text && (
                              <span>
                                Ref:{" "}
                                <span className="text-slate-700">
                                  {p.reference_text}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            </fieldset>

            {/* RBAC tab is outside the fieldset — it has its own canModifyRbac gate
                and the fieldset's disabled state would block its buttons. */}
            {activeTab === "rbac" && (
              <>
                {/* Header row: description + optional + button */}
                <div className="flex items-center justify-between gap-2 mb-3">
                  <p className="text-xs text-slate-500">
                    Role-Based Access Control. Roles currently assigned to this user
                    and the activities each role grants.
                  </p>
                  {canModifyRbac && (
                    <Button
                      type="button"
                      onClick={() => {
                        setShowAssignRole((v) => !v);
                        setSelectedRoleToAdd('');
                        setEffectiveDateToAdd('');
                        setNotesToAdd('');
                      }}
                      variant="fill"
                      icon={Plus}
                      title="Assign a role to this user"
                      data-testid="BTN_user_rbac_add"
                    >
                      Add Role
                    </Button>
                  )}
                </div>

                {/* Collapsible assign-role form */}
                {canModifyRbac && showAssignRole && (
                  <div className="mb-3 p-3 space-y-3">
                    <FormControl.SearchSelect
                      label="Role"
                      options={allRoles
                        .filter((r) => !userRbac.some((ur) => ur.id === r.id))
                        .map((r) => ({ value: String(r.id), label: r.role_name }))}
                      value={selectedRoleToAdd || null}
                      onChange={(v) => setSelectedRoleToAdd(v ? String(v) : '')}
                      placeholder={
                        rolesLoading
                          ? 'Loading roles...'
                          : allRoles.filter((r) => !userRbac.some((ur) => ur.id === r.id)).length === 0
                          ? 'All roles already assigned'
                          : 'Select a role...'
                      }
                      disabled={rolesLoading || allRoles.filter((r) => !userRbac.some((ur) => ur.id === r.id)).length === 0}
                      required
                      data-testid="SEL_user_rbac_role_select"
                    />
                    <FormControl.DatePicker
                      label="Effective Date"
                      value={effectiveDateToAdd}
                      onChange={(e) => setEffectiveDateToAdd(e.target.value)}
                      data-testid="INP_user_rbac_effective_date"
                    />
                    <FormControl.Textarea
                      label="Notes"
                      value={notesToAdd}
                      onChange={(e) => setNotesToAdd(e.target.value)}
                      rows={2}
                      placeholder="Reason for assignment, approver reference, etc."
                      data-testid="INP_user_rbac_notes"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowAssignRole(false)}
                        disabled={assigning}
                        data-testid="BTN_user_rbac_assign_cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="fill"
                        icon={Save}
                        onClick={handleAssignRole}
                        disabled={!selectedRoleToAdd || assigning || permLoading}
                        data-testid="BTN_user_rbac_assign_submit"
                      >
                        {assigning ? 'Assigning…' : 'Assign Role'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Role list */}
                {rbacLoading ? (
                  <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
                    Loading RBAC...
                  </div>
                ) : userRbac.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm">
                    <Shield className="w-8 h-8 mb-2 opacity-30" />
                    <span>No roles assigned to this user.</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {userRbac.map((role) => (
                      <div
                        key={role.id}
                        className="border border-slate-200 rounded-lg bg-white overflow-hidden"
                      >
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-100">
                          <Shield className="w-4 h-4 text-blue-600" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-blue-900">
                              {role.role_name}
                            </div>
                            {role.role_description && (
                              <div className="text-xs text-slate-500 truncate">
                                {role.role_description}
                              </div>
                            )}
                          </div>
                          <span className="status-badge xs info">
                            {role.activities.length}{" "}
                            {role.activities.length === 1 ? "activity" : "activities"}
                          </span>
                          {canModifyRbac && (
                            <div className="relative ml-1">
                              {removingRoleId === role.id ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-red-600 whitespace-nowrap">Remove role?</span>
                                  <Button
                                    type="button"
                                    variant="fill"
                                    onClick={() => handleRemoveRole(role.id)}
                                    disabled={removing}
                                    data-testid={`BTN_user_rbac_remove_confirm_${role.id}`}
                                  >
                                    {removing ? '…' : 'Yes'}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setRemovingRoleId(null)}
                                    disabled={removing}
                                    data-testid={`BTN_user_rbac_remove_cancel_${role.id}`}
                                  >
                                    No
                                  </Button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setRemovingRoleId(role.id)}
                                  className="p-1 text-slate-400 hover:text-red-500 transition-colors rounded"
                                  title={`Remove ${role.role_name} from this user`}
                                  data-testid={`BTN_user_rbac_remove_${role.id}`}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {role.activities.length === 0 ? (
                          <div className="px-6 py-3 text-xs italic text-slate-400">
                            No activities granted under this role.
                          </div>
                        ) : (
                          <ul className="pl-10 pr-4 py-2 space-y-1">
                            {role.activities.map((act) => (
                              <li
                                key={act.id}
                                className="flex items-start gap-2 text-xs text-slate-700"
                              >
                                <ActivityIcon className="w-3 h-3 mt-0.5 text-slate-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium">
                                    {act.activity_name}
                                  </span>
                                  {act.module_code && (
                                    <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">
                                      {MODULE_NAMES[act.module_code] || act.module_code}
                                    </span>
                                  )}
                                  {act.short_description && (
                                    <div className="text-[11px] text-slate-500">
                                      {act.short_description}
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === "acl" && isEditMode && user?.id && (
              <UserAclPanel userId={user.id} />
            )}
              </form>
              )}
              </div>
            </div>
          </div>
  );

  const footerContent = (
    <>
          {isEditMode && !isEntityAdmin && (
            <Button
              variant={user?.is_active ? "inactive-outline" : "active-outline"}
              icon={user?.is_active ? CircleMinus : CircleCheck}
              onClick={handleToggleActive}
              disabled={toggling || loading || !canApprove || isApproved || !isEditing}
              title={
                isApproved
                  ? "Approved records are locked. Reopen to draft first."
                  : !canApprove
                    ? approveDeniedMessage
                    : !isEditing
                      ? "Click Edit to modify this user"
                      : undefined
              }
              data-testid="BTN_user_toggle_active"
            >
              {toggling ? "..." : user?.is_active ? "Inactive" : "Active"}
            </Button>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            {isEditMode && !isEditing && !isApproved && !isEntityAdmin && canModify ? (
              <Button
                type="button"
                variant="fill"
                icon={Edit2}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsEditing(true); }}
                data-testid="BTN_user_edit"
              >
                Edit
              </Button>
            ) : isApproved || isEntityAdmin ? (
              <span
                style={{ fontSize: 13, color: "#64748b", fontStyle: "italic", alignSelf: "center" }}
                data-testid="LBL_user_readonly"
              >
                {isEntityAdmin
                  ? "Super Admin — managed in the Super Admin portal. Read-only."
                  : "Approved — read-only. Reopen to draft to edit."}
              </span>
            ) : (
              <Button
                type="submit"
                form="user-form"
                variant={isEditMode ? "update" : "fill"}
                icon={isEditMode ? Check : Save}
                disabled={loading || toggling || !!errors.login_id || !!errors.email || checkingLoginId}
                data-testid="BTN_user_submit"
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Submitting..."
                  : isEditMode
                    ? "Update"
                    : "Submit"}
              </Button>
            )}
          </div>
    </>
  );

  return (
    <>
      {asPage ? (
        <div
          className="uf-page"
          style={{
            height: "100%",
            minHeight: 0,
            marginTop: 0,
            border: "none",
            borderRadius: 0,
            boxShadow: "none",
          }}
          data-testid="PAGE_identity_profile"
        >
          {/* Title header omitted on the Identity page — the module header
              ("Identity") already labels the screen above this card. */}
          <div className="app-modal-body">{bodyContent}</div>
          <div
            className={`app-modal-footer ${isEditMode ? "app-modal-footer--split" : ""}`.trim()}
          >
            {footerContent}
          </div>
        </div>
      ) : (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <Modal.Header>{titleNode}</Modal.Header>
          <Modal.Body>{bodyContent}</Modal.Body>
          <Modal.Footer
            className={isEditMode ? "app-modal-footer--split" : ""}
          >
            {footerContent}
          </Modal.Footer>
        </Modal>
      )}

      {/* Qualification sub-modal */}
      <Modal isOpen={qualModalOpen} onClose={closeQualModal} size="md">
        <Modal.Header>
          {editingQualIdx === null ? (
            "Add Qualification"
          ) : qualIsViewMode ? (
            <>
              Viewing "
              <span className="item-highlight">
                {qualifications[editingQualIdx]?.degree_name ||
                  `Qualification #${editingQualIdx + 1}`}
              </span>
              "
            </>
          ) : (
            <>
              Editing "
              <span className="item-highlight">
                {qualifications[editingQualIdx]?.degree_name ||
                  `Qualification #${editingQualIdx + 1}`}
              </span>
              "
            </>
          )}
        </Modal.Header>
        <Modal.Body>
          <form
            id="qual-form"
            onSubmit={(e) => {
              e.preventDefault();
              saveQualModal();
            }}
            className="flex flex-col gap-y-4"
            data-testid="MOD_user_qual_form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ValidatedInput
                vh={qualVh}
                name="degree_name"
                code={109}
                label="Degree"
                value={qualDraft.degree_name}
                onChange={(e) => updateQualDraft("degree_name", e.target.value)}
                disabled={editingQualIdx !== null && qualIsViewMode}
                data-testid="INP_user_qual_degree"
              />
              <ValidatedInput
                vh={qualVh}
                name="institution_name"
                code={109}
                label="Institution"
                value={qualDraft.institution_name}
                onChange={(e) =>
                  updateQualDraft("institution_name", e.target.value)
                }
                disabled={editingQualIdx !== null && qualIsViewMode}
                data-testid="INP_user_qual_institution"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ValidatedDatePicker
                vh={qualVh}
                name="period_start"
                code={305}
                label="Start Date"
                value={qualDraft.period_start}
                onChange={(e: any) =>
                  updateQualDraft("period_start", e.target.value)
                }
                disabled={editingQualIdx !== null && qualIsViewMode}
                data-testid="INP_user_qual_period_start"
              />
              <ValidatedDatePicker
                vh={qualVh}
                name="period_end"
                code={305}
                label="End Date"
                value={qualDraft.period_end}
                additional={{ from_date: qualDraft.period_start, to_date: qualDraft.period_end }}
                onChange={(e: any) =>
                  updateQualDraft("period_end", e.target.value)
                }
                disabled={editingQualIdx !== null && qualIsViewMode}
                data-testid="INP_user_qual_period_end"
              />
              <ValidatedInput
                vh={qualVh}
                name="grade_or_marks"
                code={109}
                label="Grade/Marks"
                value={qualDraft.grade_or_marks}
                onChange={(e) =>
                  updateQualDraft("grade_or_marks", e.target.value)
                }
                disabled={editingQualIdx !== null && qualIsViewMode}
                data-testid="INP_user_qual_grade"
              />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          {editingQualIdx !== null && qualIsViewMode ? (
            <Button
              type="button"
              variant="fill"
              icon={Edit2}
              data-testid="BTN_user_qual_edit"
              onClick={(e) => { e.preventDefault(); setQualIsViewMode(false); }}
            >
              Edit
            </Button>
          ) : (
            <Button
              type="submit"
              form="qual-form"
              variant={editingQualIdx === null ? "fill" : "update"}
              icon={editingQualIdx === null ? Save : Check}
              data-testid="BTN_user_qual_submit"
            >
              {editingQualIdx === null ? "Submit" : "Update"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Experience sub-modal */}
      <Modal isOpen={expModalOpen} onClose={closeExpModal} size="md">
        <Modal.Header>
          {editingExpIdx === null ? (
            "Add Experience"
          ) : expIsViewMode ? (
            <>
              Viewing "
              <span className="item-highlight">
                {experiences[editingExpIdx]?.company_name ||
                  `Experience #${editingExpIdx + 1}`}
              </span>
              "
            </>
          ) : (
            <>
              Editing "
              <span className="item-highlight">
                {experiences[editingExpIdx]?.company_name ||
                  `Experience #${editingExpIdx + 1}`}
              </span>
              "
            </>
          )}
        </Modal.Header>
        <Modal.Body>
          <form
            id="exp-form"
            onSubmit={(e) => {
              e.preventDefault();
              saveExpModal();
            }}
            className="flex flex-col gap-y-4"
            data-testid="MOD_user_exp_form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ValidatedInput
                vh={expVh}
                name="company_name"
                code={109}
                label="Company"
                value={expDraft.company_name}
                onChange={(e) => updateExpDraft("company_name", e.target.value)}
                disabled={editingExpIdx !== null && expIsViewMode}
                data-testid="INP_user_exp_company"
              />
              <ValidatedInput
                vh={expVh}
                name="position_title"
                code={109}
                label="Position/Title"
                value={expDraft.position_title}
                onChange={(e) =>
                  updateExpDraft("position_title", e.target.value)
                }
                disabled={editingExpIdx !== null && expIsViewMode}
                data-testid="INP_user_exp_position"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ValidatedDatePicker
                vh={expVh}
                name="period_from"
                code={305}
                label="From"
                value={expDraft.period_from}
                onChange={(e: any) =>
                  updateExpDraft("period_from", e.target.value)
                }
                disabled={editingExpIdx !== null && expIsViewMode}
                data-testid="INP_user_exp_from"
              />
              <ValidatedDatePicker
                vh={expVh}
                name="period_to"
                code={305}
                label="To"
                value={expDraft.period_to}
                additional={{ from_date: expDraft.period_from, to_date: expDraft.period_to }}
                onChange={(e: any) =>
                  updateExpDraft("period_to", e.target.value)
                }
                disabled={editingExpIdx !== null && expIsViewMode}
                data-testid="INP_user_exp_to"
              />
            </div>
            <FormControl.Textarea
              label="Job Description"
              value={expDraft.job_profile_desc}
              onChange={(e) => {
                updateExpDraft("job_profile_desc", e.target.value);
                expVh.clearError("job_profile_desc");
              }}
              onBlur={(e) =>
                expVh.validateField("job_profile_desc", 110, e.target.value)
              }
              error={expVh.errors.job_profile_desc}
              rows={3}
              noResize
              disabled={editingExpIdx !== null && expIsViewMode}
              data-testid="INP_user_exp_description"
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          {editingExpIdx !== null && expIsViewMode ? (
            <Button
              type="button"
              variant="fill"
              icon={Edit2}
              data-testid="BTN_user_exp_edit"
              onClick={(e) => { e.preventDefault(); setExpIsViewMode(false); }}
            >
              Edit
            </Button>
          ) : (
            <Button
              type="submit"
              form="exp-form"
              variant={editingExpIdx === null ? "fill" : "update"}
              icon={editingExpIdx === null ? Save : Check}
              data-testid="BTN_user_exp_submit"
            >
              {editingExpIdx === null ? "Submit" : "Update"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserForm;
